import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Copy, Mail, Clock, ArrowRight, Timer } from "lucide-react";
import api from "../helpers/axios";
import ModalPortal from "./ModalPortal";
import TicketStatusPill from "./TicketStatusPill";
import TicketPriorityPill, { getPrioritySla } from "./TicketPriorityPill";
import CommentAttachment from "./CommentAttachment";

// Backend dates come in two shapes: UTC with no suffix ("...T05:39:12.483")
// and with a compact offset ("...T23:23:48+0530"). Normalize both so
// new Date() parses them correctly in every browser.
const toDate = (value) => {
  if (!value) return null;
  if (/[+-]\d{4}$/.test(value)) {
    // "+0530" → "+05:30"
    return new Date(value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
  }
  if (!value.endsWith("Z")) return new Date(value + "Z");
  return new Date(value);
};

const formatDateTime = (date) =>
  date
    ? date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "-";

// Initial-letter avatar, colored consistently per author name.
const AVATAR_COLORS = [
  "bg-[#F3EAFE] text-[#7C3AED]",
  "bg-[#E8EAFB] text-[#3F4BB8]",
  "bg-[#E7F7EE] text-[#12945A]",
  "bg-[#FEF3C7] text-[#B45309]",
  "bg-[#FCE7F3] text-[#BE185D]",
];

const Avatar = ({ name }) => {
  const charSum = [...(name || "?")].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const color = AVATAR_COLORS[charSum % AVATAR_COLORS.length];
  return (
    <div
      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${color}`}
    >
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
};

// Only these history fields are meaningful to end users. Others (like
// "Responders") carry raw Jira identifiers, so we hide them.
const SHOWN_HISTORY_FIELDS = ["status", "assignee", "resolution"];

const eyebrowClass = "text-[11px] font-semibold uppercase tracking-wider text-[#9AA1B2]";

// Pulse-bar placeholders shaped like the loaded content, shown while the
// issue-details request runs (1–3s when the backend cache is cold).
const DetailSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-6 w-2/3 rounded bg-gray-200" />
    <div className="mt-6 h-20 rounded-xl bg-gray-200" />
    <div className="mt-6 space-y-2.5">
      <div className="h-3.5 w-24 rounded bg-gray-200" />
      <div className="h-3.5 w-full rounded bg-gray-200" />
      <div className="h-3.5 w-full rounded bg-gray-200" />
      <div className="h-3.5 w-3/4 rounded bg-gray-200" />
    </div>
    <div className="mt-8 space-y-5">
      {[0, 1].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="h-8 w-8 flex-shrink-0 rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 rounded bg-gray-200" />
            <div className="h-3 w-full rounded bg-gray-200" />
            <div className="h-3 w-2/3 rounded bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Side drawer showing a support ticket in full: details, public comments,
// and change history from GET /help-center/issue-details.
const SupportTicketDrawer = ({ open, ticket, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // "notfound" | "failed"
  const [retryCount, setRetryCount] = useState(0);
  const [activeTab, setActiveTab] = useState("comments");

  // Close on Escape, like clicking the overlay.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Fetch the full ticket whenever the drawer opens (or Try Again is clicked).
  useEffect(() => {
    if (!open || !ticket?.reference_key) return;

    let cancelled = false;
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      setDetail(null);
      setActiveTab("comments");
      try {
        const res = await api.get(
          `/help-center/issue-details?key=${encodeURIComponent(ticket.reference_key)}`
        );
        if (!cancelled) setDetail(res.data?.data ?? null);
      } catch (err) {
        console.error("Failed to fetch ticket details:", err);
        if (!cancelled) {
          setError(err?.response?.status === 404 ? "notfound" : "failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetails();

    return () => {
      cancelled = true;
    };
  }, [open, ticket?.reference_key, retryCount]);

  if (!open || !ticket) return null;

  const details = detail?.details;
  const comments = detail?.comments ?? [];
  const history = (detail?.history ?? []).filter((h) =>
    SHOWN_HISTORY_FIELDS.includes(h.field?.toLowerCase())
  );
  const description = details?.description ?? ticket.description;

  const handleCopyDescription = async () => {
    try {
      await navigator.clipboard.writeText(description);
      toast.success("Description copied to clipboard");
    } catch {
      toast.error("Failed to copy description");
    }
  };

  const tabButtonClass = (tab) =>
    `rounded-lg px-4 py-2 text-xs font-semibold transition ${
      activeTab === tab
        ? "bg-white text-[#14122B] shadow-sm"
        : "text-[#8A91A5] hover:text-[#14122B]"
    }`;

  return (
    <ModalPortal>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-[#14122B]/40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-[#14122B]">Ticket Details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Reference key + status — stays put while the body scrolls */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-1 flex-shrink-0">
          <span className="inline-flex items-center rounded-lg bg-[#F3EAFE] px-3 py-1.5 text-xs font-bold text-[#7C3AED]">
            {ticket.reference_key || "-"}
          </span>
          <TicketStatusPill status={details?.status ?? ticket.status} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-navy">
          {loading ? (
            <DetailSkeleton />
          ) : error ? (
            <div className="mt-16 flex flex-col items-center text-center">
              <p className="text-sm text-gray-600">
                {error === "notfound"
                  ? "Ticket not found."
                  : "Couldn't load ticket details."}
              </p>
              {error === "failed" && (
                <button
                  onClick={() => setRetryCount((c) => c + 1)}
                  className="mt-4 rounded-full bg-[#2B3674] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#232d61] transition"
                >
                  Try Again
                </button>
              )}
            </div>
          ) : (
            detail && (
              <>
                {/* Title */}
                {details?.title && (
                  <h3 className="text-2xl font-bold leading-snug text-[#14122B] break-words">
                    {details.title}
                  </h3>
                )}

                {/* Ticket meta: requester, submission time, priority, SLA */}
                <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-5 rounded-2xl border border-[#EEF0F5] bg-[#F7F7FB] p-5">
                  <div>
                    <p className={eyebrowClass}>Submitted by</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Mail size={14} className="flex-shrink-0 text-gray-400" />
                      <a
                        href={`mailto:${details?.created_by ?? ticket.user_email}`}
                        className="text-sm font-medium text-[#14122B] hover:underline break-all"
                      >
                        {details?.created_by ?? ticket.user_email ?? "-"}
                      </a>
                    </div>
                  </div>

                  <div>
                    <p className={eyebrowClass}>Submitted on</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Clock size={14} className="flex-shrink-0 text-gray-400" />
                      <p className="text-sm font-medium text-[#14122B]">
                        {formatDateTime(toDate(details?.created_at ?? ticket.created_at))}
                      </p>
                    </div>
                  </div>

                  {(details?.priority ?? ticket.priority) && (
                    <>
                      <div>
                        <p className={eyebrowClass}>Priority</p>
                        <div className="mt-1.5">
                          <TicketPriorityPill priority={details?.priority ?? ticket.priority} />
                        </div>
                      </div>

                      <div>
                        <p className={eyebrowClass}>Resolution SLA</p>
                        <div className="mt-1.5">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3EAFE] px-3 py-1 text-xs font-semibold text-[#7C3AED]">
                            <Timer size={13} />
                            {getPrioritySla(details?.priority ?? ticket.priority) ?? "—"}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Description */}
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <p className={eyebrowClass}>Description</p>
                    {description && (
                      <button
                        onClick={handleCopyDescription}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#7C3AED] hover:text-[#6D28D9] transition"
                      >
                        <Copy size={13} />
                        Copy
                      </button>
                    )}
                  </div>
                  <p className="mt-2.5 whitespace-pre-wrap break-words text-sm leading-7 text-[#14122B]">
                    {description || "No description provided."}
                  </p>
                </div>

                {/* Comments / History tabs */}
                <div className="mt-8">
                  <div className="flex w-fit gap-1 rounded-xl bg-[#F3F4F8] p-1">
                    <button
                      onClick={() => setActiveTab("comments")}
                      className={tabButtonClass("comments")}
                    >
                      Comments ({comments.length})
                    </button>
                    <button
                      onClick={() => setActiveTab("history")}
                      className={tabButtonClass("history")}
                    >
                      History ({history.length + 1})
                    </button>
                  </div>

                  {activeTab === "comments" ? (
                    <div className="mt-5 space-y-5">
                      {comments.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          No comments on this ticket yet.
                        </p>
                      ) : (
                        comments.map((comment) => (
                          <div key={comment.id} className="flex gap-3">
                            <Avatar name={comment.author} />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-baseline gap-x-2">
                                <span className="text-sm font-semibold text-gray-800">
                                  {comment.author}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {formatDateTime(new Date(comment.created_epoch_ms))}
                                </span>
                              </div>
                              {/* Body is empty when the comment was attachments-only */}
                              {comment.body && (
                                <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-gray-700">
                                  {comment.body}
                                </p>
                              )}
                              {comment.attachments?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {comment.attachments.map((attachment) => (
                                    <CommentAttachment
                                      key={attachment.id}
                                      attachment={attachment}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="mt-5 space-y-5">
                      {history.map((change, index) => (
                        <div key={index} className="flex gap-3">
                          <Avatar name={change.author} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold text-gray-800">
                                {change.author}
                              </span>{" "}
                              changed the {change.field?.toLowerCase()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {formatDateTime(toDate(change.created_at))}
                            </p>
                            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-600">
                                {change.from ?? "None"}
                              </span>
                              <ArrowRight size={12} className="text-gray-400" />
                              <span className="rounded-full bg-[#F3EAFE] px-2.5 py-0.5 font-semibold text-[#7C3AED]">
                                {change.to ?? "None"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Creation row — not in the API's history, built from details */}
                      <div className="flex gap-3">
                        <Avatar name={details?.created_by ?? ticket.user_email} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold text-gray-800">
                              {details?.created_by ?? ticket.user_email ?? "Requester"}
                            </span>{" "}
                            created the ticket
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDateTime(toDate(details?.created_at ?? ticket.created_at))}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold rounded-xl border border-[#E4E6EF] bg-white text-[#14122B] hover:bg-gray-50 transition"
          >
            Close
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default SupportTicketDrawer;
